import { router, useLocalSearchParams } from 'expo-router';
import { jwtDecode } from 'jwt-decode';
import { useCallback, useMemo, useState } from 'react';

import { stripPhoneNumber } from '../utils';

import { cognitoSignIn } from './cognito';
import {
  AuthIdentifiers,
  AuthOptInType,
  AuthV2ChallengeType,
  ValidatedIdentifiers,
  useCreateOtpv2Mutation,
  useSignUpV2Mutation,
  useValidateOtpV2Mutation,
} from './types';
import { CreateOtpInput, IdentifyAuthJwt, LoginJwt } from './types';

export const useAuthV2 = () => {
  const [showIdentifyPopup, setShowIdentifyPopup] = useState<boolean>(false);
  const [identifyAuthJwts, setIdentifyAuthJwts] = useState<IdentifyAuthJwt[]>([]);
  const { loginJwt } = useLocalSearchParams<{ loginJwt?: string }>();

  /**
   * Get consistent login attempt parameters
   * Stub implementation for device ID for now
   */
  const persistentParameters = useMemo(
    () => ({
      deviceId: 'some-device-id-here',
      loginJwt: loginJwt,
    }),
    [loginJwt]
  );

  /**
   * GraphQL Function Callbacks
   */
  const onValidateOtp = useCallback((data, _clientOptions) => {
    // setLoginJwt(data.validateOtpV2.loginJwt);
    router.setParams({ loginJwt: data.validateOtpV2.loginJwt });
    return data;
  }, []);
  const onCreateOtp = useCallback((data, _clientOptions) => {
    //Push user to verify page & persist login JWT
    router.push({
      pathname: '/v2/signin/(verify)',
      params: { loginJwt: data.createOtpV2.loginJwt },
    });
    return data;
  }, []);
  const onSignUp = useCallback((data, _clientOptions) => {
    return data.signUpV2.loginJwt;
  }, []);

  const [internalCreateOtp] = useCreateOtpv2Mutation({ onCompleted: onCreateOtp });
  const [internalValidateOtp] = useValidateOtpV2Mutation({ onCompleted: onValidateOtp });
  const [internalSignUp] = useSignUpV2Mutation({ onCompleted: onSignUp });
  const createOtp = useCallback(
    async (partialInput: CreateOtpInput) => {
      await internalCreateOtp({
        variables: {
          input: {
            ...persistentParameters,
            ...partialInput,
          },
        },
      });
    },
    [internalCreateOtp, persistentParameters]
  );

  ////////////////////////////////////////////////////////////
  // Confirm Login
  ////////////////////////////////////////////////////////////
  const validateOtp = useCallback(
    async (otpInput: string) => {
      if (loginJwt) {
        const response = await internalValidateOtp({
          variables: { input: { otpInput, loginJwt } },
        });
        const validateResult = response.data.validateOtpV2;
        if (validateResult) {
          switch (validateResult.challengeType) {
            case AuthV2ChallengeType.SIGN_IN_CHALLENGE: {
              const { challengeCode, cognitoId, sessionId } = validateResult.challenge;
              //Authenticate with Cognito.
              const session = await cognitoSignIn(cognitoId, sessionId, challengeCode);
              //Validate the user is authenticated here
              if (session) {
                //Route to home page & clear login state
                router.navigate({ pathname: '/', params: { loginJwt: undefined } });
              }
              break;
            }
            case AuthV2ChallengeType.SIGN_UP_CHALLENGE: {
              router.push({
                pathname: '/v2/signin/(signup)',
                params: { loginJwt: validateResult.loginJwt },
              });
              break;
            }
            case AuthV2ChallengeType.IDENTIFY_CHALLENGE: {
              //Render select account popup
              const { authJwts } = validateResult.challenge;
              setIdentifyAuthJwts(
                authJwts?.map(jwt => {
                  return {
                    ...jwtDecode<IdentifyAuthJwt>(jwt),
                    jwt,
                  };
                }) ?? []
              );
              setShowIdentifyPopup(true);
              break;
            }
          }
        } else {
          throw Error('Validate OTP Error');
        }
      }
    },
    [internalValidateOtp, loginJwt]
  );
  ////////////////////////////////////////////////////////////
  // Sign Up
  ////////////////////////////////////////////////////////////
  const signUp = useCallback(
    async (name, email, phoneNumber, dob, wantsPromotionalEmail) => {
      const response = await internalSignUp({
        variables: {
          input: {
            name,
            loginJwt,
            email,
            phoneNumber: stripPhoneNumber(phoneNumber),
            dob,
            optIns: [
              {
                identifier: 'EMAIL_MARKETING' as unknown as AuthOptInType,
                optInStatus: wantsPromotionalEmail,
              },
            ],
          },
        },
      });
      const signUpResult = response.data.signUpV2;
      if (signUpResult) {
        switch (signUpResult.challengeType) {
          case AuthV2ChallengeType.SIGN_IN_CHALLENGE: {
            const { challengeCode, cognitoId, sessionId } = signUpResult.challenge;
            //Authenticate with Cognito.
            const session = await cognitoSignIn(cognitoId, sessionId, challengeCode);
            //Validate the user is authenticated here
            if (session) {
              //Push to home page and clear login state
              router.push({ pathname: '/', params: { loginJwt: undefined } });
            }
            break;
          }
          case AuthV2ChallengeType.SIGN_UP_CHALLENGE:
          case AuthV2ChallengeType.IDENTIFY_CHALLENGE: {
            throw Error("Something went wrong, these aren't valid states");
          }
        }
      }
    },
    [internalSignUp, loginJwt]
  );

  const getJwtIdentifier = useCallback(() => {
    if (loginJwt) {
      return jwtDecode<LoginJwt>(loginJwt);
    }
    //Return a dummy JWT.
    return {
      loginIdentifier: '',
      identifierType: AuthIdentifiers.EMAIL,
      validatedIdentifiers: {} as ValidatedIdentifiers,
    };
  }, [loginJwt]);

  const routeWithJWTContext = useCallback(
    (route: string) => {
      router.navigate({ pathname: route, params: { loginJwt } });
    },
    [loginJwt]
  );

  return {
    //Mutations
    createOtp,
    validateOtp,
    signUp,
    //Auth State
    identifyAuthJwts,
    setIdentifyAuthJwts,
    showIdentifyPopup,
    setShowIdentifyPopup,
    getJwtIdentifier,
    routeWithJWTContext,
  };
};
export * from './types';
